import debounce from 'lodash/debounce'
import { NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params'

import { useRef, useState } from 'react'
import useSession from '../hooks/useSession'
import useStores from '../hooks/useStores'

import UserContext from '@/gf/modules/User'
import Time from '@/gf/modules/Time'
import Select from '@/gf/modules/Select'

import NotFound from './NotFound'
import Page from '@/gf/components/Page'
import Layout from '@/gf/components/Layout'
import Card from '@/gf/components/Card'
import Frame from '../components/Frame'
import SelectField from '@/gf/components/SelectField'
import GearflowShippingMetrics from '../components/GearflowShippingMetrics'
import CancellationMetrics from '../components/CancellationMetrics'
import TabsWithContent from '@/gf/components/TabsWithContent'
import TextField from '@/gf/components/TextField'
import SpinnerSmall from '@/gf/components/SpinnerSmall'

const initToDate = new Date()
const initFromDate = new Date(initToDate)
initFromDate.setMonth(initToDate.getMonth() - 3)
const initChartPeriodDays = 28
const initialChartDataTypeId = (tabIndex: number) =>
  tabIndex === 0 ? CancellationMetrics.dataTypeOptions[0].id : ''

const useQueryParamsOpts = {
  fromDate: withDefault(StringParam, Time.dateToInput(initFromDate)),
  toDate: withDefault(StringParam, Time.dateToInput(initToDate)),
  selectedStoreId: withDefault(StringParam, ''),
  tabIndex: withDefault(NumberParam, 0),
  chartPeriodDays: withDefault(StringParam, `${initChartPeriodDays}`),
  selectedChartDataTypeId: withDefault(StringParam, initialChartDataTypeId(0)),
}

const Metrics = () => {
  const { user } = useSession()
  const stores = useStores().data?.allStores

  const [
    { fromDate, toDate, selectedStoreId, tabIndex, chartPeriodDays, selectedChartDataTypeId },
    setQuery,
  ] = useQueryParams(useQueryParamsOpts)

  const [chartPeriodDaysString, setChartPeriodDaysString] = useState(chartPeriodDays)
  const setChartPeriodDaysDebounced = useRef(
    debounce((value: string) => setQuery({ chartPeriodDays: value }), 500)
  ).current

  const chartDataTypeOptions = CancellationMetrics.dataTypeOptions

  const selectStoreOptions = Select.getSelectOptions({
    items: stores,
    showEmptyOption: true,
    showDefaultOption: true,
  })

  if (!UserContext.isAdmin(user)) {
    return <NotFound />
  }

  return (
    <Frame>
      <Page title="Metrics">
        <p className="text-sm mt-4 text-gray-500">
          Note: this page is admin-only and is not complete.
        </p>
        <Layout>
          <Layout.Section type="full">
            <Card title="Parameters">
              <Card.Section>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <TextField
                      label="From date"
                      required
                      type="date"
                      value={fromDate}
                      onChange={(e) => setQuery({ fromDate: e.target.value })}
                      max={toDate}
                    />
                    <TextField
                      label="To date"
                      required
                      type="date"
                      value={toDate}
                      onChange={(e) => setQuery({ toDate: e.target.value })}
                      min={fromDate}
                    />
                  </div>
                  {!stores ? (
                    <SpinnerSmall />
                  ) : (
                    <SelectField
                      label="Store"
                      currentId={selectedStoreId}
                      options={selectStoreOptions}
                      onChange={(storeId) => setQuery({ selectedStoreId: storeId })}
                    />
                  )}
                </div>
              </Card.Section>
              {[0].includes(tabIndex) && (
                <Card.Section title="Chart">
                  <div className="grid grid-cols-2 gap-x-2">
                    <TextField
                      label="Period days"
                      type="number"
                      step={1}
                      min={1}
                      value={chartPeriodDaysString}
                      onChange={(e) => {
                        setChartPeriodDaysString(e.target.value)
                        setChartPeriodDaysDebounced(e.target.value)
                      }}
                    />
                    <SelectField
                      label="Data"
                      options={chartDataTypeOptions}
                      currentId={selectedChartDataTypeId}
                      onChange={(newId) => setQuery({ selectedChartDataTypeId: newId })}
                    />
                  </div>
                </Card.Section>
              )}
            </Card>
            <TabsWithContent
              selectedIndex={tabIndex}
              onChange={(index) =>
                setQuery({
                  tabIndex: index,
                  selectedChartDataTypeId: initialChartDataTypeId(index),
                })
              }
              tabs={[
                {
                  title: 'Cancellations',
                  children: (
                    <CancellationMetrics
                      fromDate={fromDate}
                      toDate={toDate}
                      selectedStoreId={selectedStoreId}
                      chartPeriodDays={parseInt(chartPeriodDays, 10) || initChartPeriodDays}
                      chartDataType={selectedChartDataTypeId}
                    />
                  ),
                },
                {
                  title: 'Gearflow shipping',
                  children: (
                    <GearflowShippingMetrics
                      fromDate={fromDate}
                      toDate={toDate}
                      selectedStoreId={selectedStoreId}
                    />
                  ),
                },
              ]}
            />
          </Layout.Section>
        </Layout>
      </Page>
    </Frame>
  )
}

export default Metrics
